import React, { useState, useEffect, useRef } from 'react';
import { ExternalLink, Info, Sparkles, Heart, Lock, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { track } from '../lib/analytics';
import { recordApplication } from '../utils/applicationTracking';
import { useAIReachout, getReachoutUsage, FREE_REACHOUT_CREDITS } from '../hooks/useAIReachout';
import ConfidenceDonut from './ConfidenceDonut';

const OfferDetailView = ({ offer, isFavorite = false, onToggleFavorite }) => {
    const [iaMessage, setIaMessage] = useState('');
    const [showInfo, setShowInfo] = useState(false);
    const scrollRef = useRef(null);
    const textareaRef = useRef(null);
    const navigate = useNavigate();
    const { user } = useAuth();

    // Premium & Tracking State
    const [isPremium, setIsPremium] = useState(false);
    const [usageCount, setUsageCount] = useState(0);
    const [isLoadingUsage, setIsLoadingUsage] = useState(false);
    const [isSending, setIsSending] = useState(false);

    // Offer Insights State
    const [offerInsights, setOfferInsights] = useState(null);
    const [insightsLoading, setInsightsLoading] = useState(false);
    const [hasInsights, setHasInsights] = useState(false); // New state to check existence for non-premium

    // Fetch Premium Status & Usage
    useEffect(() => {
        const fetchStatus = async () => {
            if (!user) return;

            // Check Premium
            const { data } = await supabase.from('profiles').select('is_premium').eq('id', user.id).single();
            const premiumStatus = data?.is_premium || false;
            setIsPremium(premiumStatus);

            // Check remaining free reachout credits if Premium
            if (premiumStatus) {
                setIsLoadingUsage(true);
                const { count } = await getReachoutUsage(user.id);
                setUsageCount(count);
                setIsLoadingUsage(false);
            }
        };
        fetchStatus();
    }, [user, offer?.id]);

    // Fetch offer insights (Premium) OR check existence (Non-Premium)
    useEffect(() => {
        if (!offer?.id) return;

        const fetchInsights = async () => {
            setInsightsLoading(true);
            try {
                if (isPremium) {
                    // Premium: Get full data
                    const { data, error } = await supabase.rpc('get_offer_insights', {
                        p_id_offre: offer.id
                    });
                    if (error) throw error;
                    setOfferInsights(data);
                    // Only show if confidence > 70
                    setHasInsights(data && data.confidence_score > 70);
                } else {
                    // Non-Premium: Check existence only (via new secure function)
                    const { data, error } = await supabase.rpc('get_offer_has_insights', {
                        p_id_offre: offer.id
                    });
                    if (error) {
                        console.warn("Could not check insights existence (RPC missing?)", error);
                        // If check fails, defaulting to hidden is safer to avoid empty locked box
                        setHasInsights(false);
                    } else {
                        setHasInsights(data);
                    }
                }
            } catch (err) {
                console.error('Error fetching offer insights:', err);
                setOfferInsights(null);
                setHasInsights(false);
            } finally {
                setInsightsLoading(false);
            }
        };

        // If isPremium changed, re-fetch? Yes.
        fetchInsights();
    }, [offer?.id, isPremium]);

    // Handle "Envoyer votre profil"
    const handleApply = async () => {
        if (!isPremium || isSending) return;

        setIsSending(true);
        const { success, message, newCount } = await recordApplication(user.id, offer.id);

        if (success) {
            setUsageCount(newCount);
            alert(message); // Replace with a nice toast if available
            setIaMessage(''); // Clear input
        } else {
            alert(message);
        }
        setIsSending(false);
    };

    // Reset scroll when offer changes

    // Move hook to top level
    const { generateReachout, isGenerating } = useAIReachout();

    // Suit l'offre pour laquelle une génération IA est en cours : si l'utilisateur
    // change d'offre avant la fin de l'appel, on ignore le résultat à son retour
    // au lieu de l'afficher sur la mauvaise offre.
    const activeOfferIdRef = useRef(offer?.id);

    // Reset scroll, puis restaure le mail IA déjà généré pour cette offre (persisté en
    // base) au lieu de le vider : sinon un F5 ou un changement d'offre le fait perdre.
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
        const requestedOfferId = offer?.id;
        activeOfferIdRef.current = requestedOfferId;
        setIaMessage('');

        if (!user || !requestedOfferId || offer?.isExclusive) return;
        supabase
            .from('user_offer_activities')
            .select('ai_reachout_message')
            .eq('user_id', user.id)
            .eq('offer_id', requestedOfferId)
            .maybeSingle()
            .then(({ data }) => {
                if (activeOfferIdRef.current === requestedOfferId && data?.ai_reachout_message) {
                    setIaMessage(data.ai_reachout_message);
                }
            });
    }, [offer?.id, offer?.isExclusive, user]);

    // Auto-agrandit le textarea du mail IA pour afficher tout le texte généré
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [iaMessage]);

    if (!offer) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 p-10 bg-white rounded-[32px] border border-gray-100">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                    <Info className="w-8 h-8 opacity-50" />
                </div>
                <p className="font-semibold text-lg">Sélectionnez une offre pour voir les détails</p>
            </div>
        );
    }

    // Use local flags if available, fallback to flagcdn
    const countryCode = (offer?.countryCode && typeof offer.countryCode === 'string') ? offer.countryCode : 'FR';
    const flagUrl = `/assets/flags/no_background/${countryCode.toUpperCase()}.png`;

    // Scale scaling logic similar to Onboarding for better fit
    const getScaleClass = (code) => {
        const c = code.toUpperCase();
        if (c === 'DE') return 'scale-[1.8]';
        if (c === 'MX') return 'scale-[1.7]';
        if (c === 'CH') return 'scale-[1.6]';
        return 'scale-[1.4]';
    };

    const handleAiGenerate = async () => {
        if (isGenerating || !user) return;

        const requestedOfferId = offer.id;
        const generatedText = await generateReachout(user.id, offer);

        // L'utilisateur a changé d'offre pendant la génération : on jette le résultat.
        if (activeOfferIdRef.current !== requestedOfferId) return;

        if (generatedText) {
            setIaMessage(generatedText);
            setUsageCount(c => c + 1);

            // Ajoute systématiquement l'offre aux favoris et persiste le texte généré,
            // pour qu'il ne soit pas perdu si l'utilisateur quitte la page ou fait F5.
            if (!isFavorite) onToggleFavorite(offer.id);
            supabase
                .from('user_offer_activities')
                .upsert({
                    user_id: user.id,
                    offer_id: requestedOfferId,
                    is_favorite: true,
                    is_viewed: true,
                    ai_reachout_message: generatedText,
                    updated_at: new Date(),
                }, { onConflict: 'user_id, offer_id' })
                .then(({ error: upsertError }) => {
                    if (upsertError) console.error('Error saving AI reachout message:', upsertError);
                });
        } else if (error) {
            alert(error); // Show limit hit or other error
        }
    };

    return (
        <div className="bg-white rounded-[32px] border border-gray-100 h-[calc(100vh-140px)] flex flex-col shadow-sm overflow-hidden sticky top-[120px]">
            <div ref={scrollRef} className="p-8 overflow-y-auto custom-scrollbar flex-1">
                {/* Header: Flag, Title, Company */}
                <div className="flex items-start gap-6 mb-8">
                    <div className="w-[80px] h-[80px] rounded-full overflow-hidden border-[3px] border-white shadow-lg flex-shrink-0 relative bg-slate-50">
                        <img
                            src={flagUrl}
                            alt={offer.country}
                            className={`w-full h-full object-cover transform ${getScaleClass(countryCode)}`}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://flagcdn.com/w160/${countryCode.toLowerCase()}.png`;
                                e.target.className = "w-full h-full object-cover scale-125"; // Fallback scale
                            }}
                        />
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-[#1e293b] leading-tight mb-1">
                            {offer.title}
                        </h2>
                        <h3 className="text-xl font-bold text-[#64748b]">
                            {offer.company}
                        </h3>
                    </div>
                    <div className="flex flex-col items-end gap-2 ml-auto">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleFavorite && onToggleFavorite(offer.id);
                            }}
                            className="w-12 h-12 rounded-full flex items-center justify-center transition-all flex-shrink-0 group hover:bg-slate-50"
                            title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
                        >
                            <Heart
                                className={`w-6 h-6 transition-colors ${isFavorite ? 'fill-blue-500 text-blue-500' : 'text-black fill-transparent group-hover:text-blue-500'}`}
                                strokeWidth={2}
                            />
                        </button>

                        {/* Open in new tab button */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (offer.isExclusive) {
                                    window.open(`/offres-plus?id=${offer.id}`, '_blank');
                                } else {
                                    window.open(`/offres_vie/${offer.id}`, '_blank');
                                }
                            }}
                            className="w-10 h-10 rounded-full flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-slate-50 transition-all"
                            title="Ouvrir dans un nouvel onglet"
                        >
                            <ExternalLink size={20} />
                        </button>
                    </div>
                </div>

                {/* Quick Info Tags */}
                <div className="flex flex-wrap gap-3 mb-8">
                    <span className="px-4 py-2 rounded-full bg-[#f1f5f9] text-[#334155] text-sm font-bold border border-slate-200">
                        {offer.country}
                    </span>
                    <span className="px-4 py-2 rounded-full bg-[#f1f5f9] text-[#334155] text-sm font-bold border border-slate-200">
                        {offer.city}
                    </span>
                    <span className="px-4 py-2 rounded-full bg-[#e0f2fe] text-[#0284c7] text-sm font-bold border border-blue-100">
                        {offer.sector}
                    </span>
                    <span className="px-4 py-2 rounded-full bg-[#e0f2fe] text-[#0284c7] text-sm font-bold border border-blue-100">
                        {offer.duration}
                    </span>
                    {/* Languages Label - Only show if languages exist and are not empty */}
                    {offer.langues_requises && (() => {
                        const cleaned = offer.langues_requises.replace(/[\[\]"]/g, '').trim();
                        return cleaned ? (
                            <span className="px-4 py-2 rounded-full bg-purple-50 text-purple-600 text-sm font-bold border border-purple-100">
                                {cleaned.split(',').slice(0, 3).join(', ')}
                            </span>
                        ) : null;
                    })()}
                </div>

                {/* HTML Sections */}
                <div className="prose prose-slate prose-sm max-w-none mb-8 text-[#475569] space-y-8">
                    {/* Fallback for legacy data */}
                    {!offer.companyHtml && !offer.missionHtml && !offer.profileHtml && (
                        <div dangerouslySetInnerHTML={{ __html: offer.description }} />
                    )}

                    {offer.companyHtml && (
                        <div>
                            <h3 className="text-xl font-bold text-blue-600 mb-4">L'entreprise</h3>
                            <div dangerouslySetInnerHTML={{ __html: offer.companyHtml }} />
                        </div>
                    )}

                    {offer.missionHtml && (
                        <div>
                            <h3 className="text-xl font-bold text-blue-600 mb-4">La mission</h3>
                            <div dangerouslySetInnerHTML={{ __html: offer.missionHtml }} />
                        </div>
                    )}

                    {offer.profileHtml && (
                        <div>
                            <h3 className="text-xl font-bold text-blue-600 mb-4">Profil recherché</h3>
                            <div dangerouslySetInnerHTML={{ __html: offer.profileHtml }} />
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex mb-8">
                    <a
                        href={offer.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => track('offer_clicked_external', { offer_id: offer.id, source: offer.isExclusive ? 'hors_bf' : 'bf', device: 'desktop' })}
                        className="flex items-center gap-2 bg-[#0F172A] hover:bg-[#1e293b] text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg shadow-slate-900/20 active:scale-95 group"
                    >
                        <span>Redirection vers l'offre</span>
                        <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </a>
                </div>

                {/* Candidature Simplifiée Box - Premium Style like Mobile */}
                {!offer.isExclusive && (
                    <div className="bg-violet-400/15 rounded-[24px] p-6 relative overflow-visible mt-auto border border-violet-200">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                {/* Premium Badge */}
                                <div className="flex items-center justify-center px-3 py-1.5 bg-slate-900 rounded-full">
                                    <img
                                        src="/assets/Trouve+white.png"
                                        alt="Trouve+"
                                        className="h-5 w-auto object-contain"
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <span className="font-extrabold text-violet-700 text-lg leading-tight">Candidature simplifiée</span>
                                    {isPremium && !isLoadingUsage && (
                                        <span className="text-[11px] font-bold text-violet-400">
                                            {usageCount >= FREE_REACHOUT_CREDITS
                                                ? 'Essais gratuits épuisés'
                                                : `${FREE_REACHOUT_CREDITS - usageCount} essai${FREE_REACHOUT_CREDITS - usageCount > 1 ? 's' : ''} gratuit${FREE_REACHOUT_CREDITS - usageCount > 1 ? 's' : ''} restant${FREE_REACHOUT_CREDITS - usageCount > 1 ? 's' : ''}`}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="relative">
                                <button onClick={() => setShowInfo(!showInfo)} className="focus:outline-none">
                                    <Info className={`w-5 h-5 transition-colors ${showInfo ? 'text-violet-600' : 'text-slate-400'}`} />
                                </button>

                                {showInfo && (
                                    <div className="absolute bottom-full right-0 mb-3 w-[290px] bg-white rounded-2xl p-5 shadow-xl border border-gray-100 z-50 animate-in fade-in zoom-in-95 duration-200 text-left">
                                        <div className="text-sm text-[#64748b] space-y-3 font-medium leading-relaxed">
                                            <div className="flex items-center gap-2 mb-1">
                                                <div className="flex items-center justify-center px-2 py-1 bg-slate-900 rounded-full">
                                                    <img src="/assets/Trouve+white.png" className="h-3.5" alt="Trouve+" />
                                                </div>
                                                <span className="font-bold text-violet-600">Candidature simplifiée</span>
                                            </div>
                                            <p>
                                                Notre IA rédige instantanément un message de candidature sur-mesure. Elle s'appuie sur votre CV et les prérequis de l'offre pour maximiser vos chances de réussite.
                                            </p>
                                            {/* <p className="font-bold text-violet-700">
                                                Un e-mail de candidature sera envoyé directement au recruteur, avec votre CV et vos coordonnées.
                                            </p> */}
                                        </div>
                                        <div className="absolute -bottom-2 right-1.5 w-4 h-4 bg-white border-b border-r border-gray-100 transform rotate-45"></div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Input Area and Button */}
                        <div className="flex items-end gap-3">
                            {isPremium ? (
                                <div className="flex-1 bg-white rounded-2xl p-2 pl-4 flex items-end gap-3 shadow-sm border border-gray-200">
                                    <textarea
                                        rows={1}
                                        ref={textareaRef}
                                        placeholder={
                                            usageCount >= FREE_REACHOUT_CREDITS
                                                ? "Tu as utilisé tes essais gratuits de candidature simplifiée"
                                                : (isGenerating ? "L'IA rédige votre email..." : "Génère un mail de reach-out optimisé pour les VIE grâce à ton CV et à notre IA")
                                        }
                                        className="flex-1 bg-transparent text-xs text-slate-700 placeholder-slate-400 focus:outline-none py-2 resize-none max-h-[240px] overflow-y-auto leading-relaxed"
                                        value={iaMessage}
                                        onChange={(e) => setIaMessage(e.target.value)}
                                        disabled={isGenerating || usageCount >= FREE_REACHOUT_CREDITS}
                                    />
                                    <button
                                        onClick={handleAiGenerate}
                                        disabled={isGenerating || usageCount >= FREE_REACHOUT_CREDITS}
                                        className={`
                                        h-10 w-10 flex items-center justify-center rounded-[14px] transition-all relative overflow-hidden group flex-shrink-0
                                        ${isGenerating
                                                ? 'bg-[#0F172A] shadow-[0_0_15px_rgba(139,92,246,0.6)] border border-violet-500/50 animate-pulse cursor-wait'
                                                : (usageCount >= FREE_REACHOUT_CREDITS ? 'bg-gray-100/80 text-gray-300 cursor-not-allowed' : (iaMessage ? 'bg-[#0F172A] text-white hover:bg-black shadow-md' : 'bg-gray-100/80 text-gray-400 hover:bg-gray-200 hover:text-gray-600'))
                                            }
                                    `}
                                    >
                                        <svg width="0" height="0" className="absolute">
                                            <defs>
                                                <linearGradient id="ai-gradient-desktop" x1="0%" y1="0%" x2="100%" y2="100%">
                                                    <stop offset="0%" stopColor="#8b5cf6" />
                                                    <stop offset="100%" stopColor="#3b82f6" />
                                                </linearGradient>
                                            </defs>
                                        </svg>
                                        <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full transition-all duration-1000 ${isGenerating ? 'animate-[shimmer_1.5s_infinite]' : 'group-hover:animate-[shimmer_1.5s_infinite]'}`} />
                                        <Sparkles
                                            className={`w-5 h-5 relative z-10 transition-all ${isGenerating ? 'scale-110 drop-shadow-[0_0_5px_rgba(139,92,246,0.8)]' : ''}`}
                                            strokeWidth={isGenerating ? 2.5 : 2}
                                            style={isGenerating ? { stroke: "url(#ai-gradient-desktop)", color: "transparent" } : {}}
                                        />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex-1 px-2">
                                    <p className="text-xs text-slate-600 font-medium leading-relaxed text-justify">
                                        <span className="font-bold text-violet-700">L'IA Trouve+ rédige pour vous</span> un email de candidature optimisé et personnalisé.
                                        {/* <br />
                                        Nous nous chargeons d'envoyer <span className="font-bold">votre profil, votre CV et vos coordonnées</span> directement au recruteur. */}
                                    </p>
                                </div>
                            )}

                            {isPremium ? (
                                null /* Temporarily disabled
                                <button
                                    onClick={handleApply}
                                    disabled={usageCount >= 5 || isSending}
                                    className={`
                                    rounded-xl shadow-lg transition-all h-[56px] flex-shrink-0 px-3 font-bold text-[11px] leading-tight text-white flex flex-col items-center justify-center min-w-[90px]
                                    ${usageCount >= 5
                                            ? 'bg-slate-400 cursor-not-allowed'
                                            : 'bg-[#0F172A] hover:bg-[#1e293b] active:scale-95'
                                        }
                                `}
                                >
                                    {isSending ? (
                                        "Envoi..."
                                    ) : (
                                        usageCount >= 5 ? (
                                            <>
                                                <span>Limite</span>
                                                <span>atteinte (5/5)</span>
                                            </>
                                        ) : (
                                            <>
                                                <span>Envoyer votre</span>
                                                <span>profil ({usageCount}/5)</span>
                                            </>
                                        )
                                    )}
                                </button>
                                */
                            ) : (
                                <button
                                    onClick={() => navigate('/trouve-plus')}
                                    className="bg-[#0F172A] hover:bg-[#1e293b] text-white rounded-xl shadow-lg transition-all h-[56px] flex-shrink-0 px-6 group relative overflow-hidden"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <div className="flex flex-col items-center justify-center leading-tight relative z-10">
                                        <span className="text-xs font-bold mb-0.5">Débloquer</span>
                                        <div className="flex items-center gap-1">
                                            <span className="font-extrabold text-sm">Trouve</span>
                                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 font-black text-sm">+</span>
                                        </div>
                                    </div>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* ===== INSIGHTS SECTION ===== */}
                {hasInsights && (
                    <div className="bg-gradient-to-br from-blue-50/80 to-violet-50/80 rounded-[24px] p-6 mt-4 border border-violet-100/40">
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex items-center justify-center px-3 py-1.5 bg-slate-900 rounded-full">
                                <img src="/assets/Trouve+white.png" alt="Trouve+" className="h-5 w-auto object-contain" />
                            </div>
                            <span className="font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600 text-lg">
                                Insights
                            </span>
                        </div>

                        {!isPremium ? (
                            /* Non-premium locked state */
                            <div className="flex flex-col gap-3">
                                <div className="flex items-start gap-4 py-1">
                                    <div className="w-10 h-10 bg-white/80 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                        <Lock className="w-5 h-5 text-slate-400" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm text-slate-700 font-medium leading-relaxed mb-2">
                                            Découvrez comment cette entreprise recrute et identifiez les meilleurs contacts LinkedIn pour postuler.
                                        </p>
                                        <p className="text-xs text-slate-500 font-medium">Disponible avec Trouve+</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => navigate('/trouve-plus')}
                                    className="w-full py-2.5 bg-slate-900 text-white rounded-xl text-sm font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
                                >
                                    <span>Débloquer avec</span>
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-violet-300 font-extrabold">Trouve+</span>
                                </button>
                            </div>
                        ) : insightsLoading ? (
                            <div className="flex items-center justify-center py-6">
                                <div className="w-6 h-6 border-2 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
                            </div>
                        ) : (
                            /* Has insights (guaranteed due to hasInsights check) */
                            <div className="flex items-start gap-4">
                                <div className="flex-1">
                                    <p className="text-sm text-slate-700 leading-relaxed">
                                        Les recrutements de VIE sont gérés par des{' '}
                                        <span className="font-bold text-violet-700">{offerInsights?.contact_linkedin_supposed_role_type}</span>
                                        {offerInsights?.contact_location && (
                                            <span>
                                                {offerInsights.contact_location === 'France'
                                                    ? ' basés en France (siège)'
                                                    : ' basés en local (pays du VIE)'}
                                            </span>
                                        )}.
                                    </p>
                                    <p className="text-sm text-slate-700 leading-relaxed mt-2">
                                        Pour un reachout sur LinkedIn, nous vous conseillons de contacter les{' '}
                                        <span className="font-bold text-violet-700">
                                            {offerInsights?.contact_linkedin_supposed_role_type}
                                            {offerInsights?.contact_linkedin_supposed_role && (
                                                <span className="font-semibold text-slate-600"> ({offerInsights.contact_linkedin_supposed_role})</span>
                                            )}
                                        </span>{' '}
                                        chez{' '}
                                        <span className="font-bold text-slate-900">{offerInsights?.search_company_name || offer.company}</span>.
                                    </p>
                                </div>
                                <div className="flex-shrink-0 relative">
                                    <ConfidenceDonut
                                        score={offerInsights?.confidence_score}
                                        size={70}
                                        strokeWidth={7}
                                        label="Confiance"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OfferDetailView;
